import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { 
  Bars3Icon, 
  XMarkIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const cycleTheme = () => {
    const themes = ['dark', 'light'] as const;
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <SunIcon className="h-5 w-5" />;
      case 'dark':
        return <MoonIcon className="h-5 w-5" />;
      default:
        return <MoonIcon className="h-5 w-5" />;
    }
  };

  // Helper function to determine if a link is active
  const isActiveLink = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/session/');
    }
    return location.pathname === path;
  };

  // Helper function to get link classes
  const getLinkClasses = (path: string, isMobile = false) => {
    const baseClasses = isMobile 
      ? "block px-3 py-2 rounded-md text-base font-medium transition-colors"
      : "px-3 py-2 rounded-md text-sm font-medium transition-colors";
    
    const isActive = isActiveLink(path);
    
    if (isActive) {
      return `${baseClasses} bg-twitch-600 text-white light:bg-twitch-100 light:text-twitch-800`;
    } else {
      return `${baseClasses} text-gray-300 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100`;
    }
  };

  return (
    <nav className="bg-gray-800 light:bg-white border-b border-gray-700 light:border-gray-200 fixed w-full z-50 top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Logo */}
            <div className="flex items-center">
              <Link to={user ? "/dashboard" : "/"} className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-twitch-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">W</span>
                </div>
                <span className="text-xl font-bold text-white light:text-gray-900">Waddup.me</span>
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link
                  to="/dashboard"
                  className={getLinkClasses('/dashboard')}
                >
                  {t('navbar.dashboard')}
                </Link>
                <Link
                  to="/statistics"
                  className={getLinkClasses('/statistics')}
                >
                  {t('navbar.statistics')}
                </Link>
                <Link
                  to="/settings"
                  className={getLinkClasses('/settings')}
                >
                  {t('navbar.settings')}
                </Link>
                <Link
                  to="/obs"
                  className={getLinkClasses('/obs')}
                >
                  OBS
                </Link>
                {user?.isAdmin && (
                  <Link
                    to="/admin"
                    className={getLinkClasses('/admin')}
                  >
                    Admin
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="p-2 rounded-lg text-gray-400 light:text-gray-500 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 transition-colors"
              title={`Current theme: ${theme}`}
            >
              {getThemeIcon()}
            </button>

            {/* User Info */}
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full overflow-hidden bg-twitch-600 flex items-center justify-center">
                {user?.profileImage ? (
                  <img 
                    src={user.profileImage} 
                    alt={user.displayName || user.username} 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-white">
                    {user?.displayName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <span className="text-sm font-medium text-white light:text-gray-900">
                {user?.displayName || user?.username}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm font-medium text-gray-300 light:text-gray-600 hover:text-white light:hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-700 light:hover:bg-gray-100 transition-colors"
              >
                {t('navbar.logout')}
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-gray-400 light:text-gray-500 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-800 light:bg-white border-t border-gray-700 light:border-gray-200">
            <Link
              to="/dashboard"
              className={getLinkClasses('/dashboard', true)}
              onClick={() => setIsOpen(false)}
            >
              {t('navbar.dashboard')}
            </Link>
            <Link
              to="/statistics"
              className={getLinkClasses('/statistics', true)}
              onClick={() => setIsOpen(false)}
            >
              {t('navbar.statistics')}
            </Link>
            <Link
              to="/settings"
              className={getLinkClasses('/settings', true)}
              onClick={() => setIsOpen(false)}
            >
              {t('navbar.settings')}
            </Link>
            <Link
              to="/obs"
              className={getLinkClasses('/obs', true)}
              onClick={() => setIsOpen(false)}
            >
              OBS
            </Link>
            {user?.isAdmin && (
              <Link
                to="/admin"
                className={getLinkClasses('/admin', true)}
                onClick={() => setIsOpen(false)}
              >
                Admin
              </Link>
            )}
            
            {/* Mobile Theme Toggle */}
            <button
              onClick={() => {
                cycleTheme();
                setIsOpen(false);
              }}
              className="w-full text-left flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-300 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100"
            >
              <span className="mr-3">{getThemeIcon()}</span>
              Theme: {theme.charAt(0).toUpperCase() + theme.slice(1)}
            </button>
            
            <div className="border-t border-gray-700 light:border-gray-200 pt-4">
              <div className="flex items-center px-3 py-2">
                <div className="h-8 w-8 rounded-full overflow-hidden bg-twitch-600 flex items-center justify-center mr-3">
                  {user?.profileImage ? (
                    <img 
                      src={user.profileImage} 
                      alt={user.displayName || user.username} 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-white">
                      {user?.displayName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="text-base font-medium text-white light:text-gray-900">
                  {user?.displayName || user?.username}
                </span>
              </div>
              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100"
              >
                {t('navbar.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 